import Alpine from "alpinejs";

import { Cart, LineItem, Product } from "@app/types";
import { cart } from "@app/lib/cart";
import { storefront } from "@app/lib/storefront";

import { GET_PRODUCT_BY_HANDLE } from "@app/gql/queries";
import { CART_EVENTS } from "@app/lib/events";
import { cloud } from "@app/lib/cloud";

type Variant = {
  selectedOptions: {
    name: string;
    value: string;
  }[];
};

const store = {
  init() {
    this.handleUpdateCart();
    this.attachCustomerAttributes();
    document.addEventListener(CART_EVENTS.MODIFIED, this.handleUpdateCart.bind(this));
  },
  cartFetched: false as boolean,
  value: {} as Cart,
  items: [] as LineItem[],
  isUpdating: false,

  async handleUpdateCart() {
    this.isUpdating = true;
    const value = await cart.getCart();
    this.isUpdating = false;
    if (!value) {
      this.cartFetched = true;
      return;
    }
    this.value = value;
    this.items = value.items;
    this.cartFetched = true;
    document.dispatchEvent(new CustomEvent(CART_EVENTS.FETCHED));
  },

  get subtotal() {
    return this?.value?.total_price ?? 0;
  },

  get totalItems() {
    return this?.value?.item_count ?? window.SHOPIFY_DATA.cart.item_count;
  },

  createUpdateWrapper(callback: () => Promise<any>) {
    return async () => {
      this.isUpdating = true;
      await callback();
      this.isUpdating = false;
    };
  },

  async updateLineItemQuantity(id: number, quantity: number) {
    if (quantity < 0) return;
    const handler = async () => await cart.updateLineItemQuantity(id, quantity);
    await this.createUpdateWrapper(handler)();
  },

  async removeLineItem(id: number) {
    const handler = async () => await cart.removeLineItem(id);
    await this.createUpdateWrapper(handler)();
  },

  async replaceLineItem(targetId: number, replacementId: number) {
    const handler = async () => await cart.replaceLineItem(targetId, replacementId);
    await this.createUpdateWrapper(handler)();
  },

  async attachCustomerAttributes() {
    if (!window.SHOPIFY_DATA.customer.loggedIn) return;

    const attributes: Record<string, string> = {};

    const hasIDAttribute = !!this.value.attributes?.antavo_id;
    const hasMemberAttribute = !!this.value.attributes?.antavo_member_id;
    const hasAccountType = !!this.value.attributes?.account_type;

    if (!hasAccountType) {
      const isStaffMember = window.SHOPIFY_DATA.customer.tags.includes("staff");
      if (isStaffMember) attributes["account_type"] = "staff";
    }

    if (!hasIDAttribute || !hasMemberAttribute) {
      const profile = await cloud.fetchCustomerProfile();
      attributes["antavo_id"] = profile.id;
      attributes["antavo_membership_id"] = profile?.properties?.kmd_membership_id;
    }

    const handler = async () => await cart.updateCartAttributes(attributes);
    await this.createUpdateWrapper(handler)();
  },

  async getProducts(handle: string) {
    try {
      const response = await storefront.query({
        query: GET_PRODUCT_BY_HANDLE,
        variables: {
          handle,
        },
      });

      return response.data?.product;
    } catch (error) {
      console.error("Error fetching product:", error);
      throw error;
    }
  },

  getVariantOption(variant: Variant, option: string) {
    return variant.selectedOptions.find((selectedOption) => selectedOption.name === option)?.value;
  },
};

Alpine.store("cart", store);
